import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Button, Divider, Group, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { ApiError, api } from '../api';
import { formatDate } from '../format';
import { EditableText } from '../ui/EditableText';

/**
 * One's own passkeys (AP-24): sign in with fingerprint, face or device PIN
 * instead of the password. One per device; each can be revoked on its own,
 * e.g. when a phone is lost. The password stays as the way back in.
 */
export function PasskeysSection(): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['passkeys'], queryFn: api.passkeys });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['passkeys'] });
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: async () => {
      const optionsJSON = await api.passkeyRegisterOptions();
      const response = await startRegistration({ optionsJSON });
      return api.passkeyRegister(response, name.trim());
    },
    onSuccess: () => setName(''),
    onSettled: refresh,
  });
  const rename = useMutation({ mutationFn: ({ id, name: next }: { id: string; name: string }) => api.renamePasskey(id, next), onSettled: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api.removePasskey(id), onSettled: refresh });
  const error = add.error ?? rename.error ?? remove.error;
  const supported = browserSupportsWebAuthn();

  return (
    <Stack gap="xs">
      <Divider label="Passkeys" labelPosition="left" />
      <Text size="xs" c="dimmed">
        Anmelden mit Fingerabdruck, Gesicht oder Geräte-PIN statt Passwort – fest an diese Adresse gebunden, daher
        nicht abzufischen. Je Gerät ein Passkey; das Passwort bleibt als Rückweg.
      </Text>
      {error && (
        <Alert color="red" py={6} withCloseButton onClose={() => [add, rename, remove].forEach((m) => m.reset())}>
          {error instanceof ApiError ? error.message : 'Abgebrochen – oder dieses Gerät hat schon einen Passkey für PST.'}
        </Alert>
      )}
      {(list.data ?? []).map((p) => (
        <Group key={p.id} justify="space-between" wrap="nowrap" gap="xs">
          <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
            <EditableText value={p.name} onCommit={(next) => rename.mutate({ id: p.id, name: next })} title="Namen ändern" />
            <Text size="xs" c="dimmed">eingerichtet {formatDate(p.createdAt)} · {p.lastUsedAt ? `zuletzt ${formatDate(p.lastUsedAt)}` : 'noch nicht benutzt'}</Text>
          </Stack>
          <ActionIcon
            variant="subtle" color="red" aria-label={`Passkey ${p.name} widerrufen`} title="Widerrufen"
            onClick={() => { if (window.confirm(`Passkey „${p.name}“ widerrufen? Mit diesem Gerät ist dann nur noch die Anmeldung per Passwort möglich.`)) remove.mutate(p.id); }}
          >✕</ActionIcon>
        </Group>
      ))}
      {list.isSuccess && list.data.length === 0 && <Text size="sm" c="dimmed">Noch kein Passkey eingerichtet.</Text>}
      {supported ? (
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) add.mutate(); }}>
          <Group gap="xs" align="flex-end">
            <TextInput size="xs" label="Name des Geräts" placeholder="z. B. Laptop, iPhone" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1 }} />
            <Button size="xs" type="submit" variant="light" disabled={!name.trim()} loading={add.isPending}>Passkey einrichten</Button>
          </Group>
        </form>
      ) : (
        <Text size="xs" c="orange.7">Dieser Browser unterstützt keine Passkeys.</Text>
      )}
    </Stack>
  );
}
